import React from "react";
import {
  WmProgressIndicator,
  WmProgressMonitor,
  WmProgressSlice
} from "@watermarkinsights/ripple-react";
import "../../../common/Insights.scss";
import translate from "../../../../i18n/translate";
import { useIntl } from "react-intl";
import { MissionAndOutcomesHtmlState } from "./MissionAndOutcomesExportView";
import { popover } from "./CoursesChart";
import ApiError from "@/features/common/elements/ApiError";
import { OutcomesTerminology } from "@/contexts/AppContext";
import { useFormatMessage } from "@/features/common/helper";

interface OrganizationChartProps {
  organizationData: MissionAndOutcomesHtmlState;
  outcomesTerminology?: OutcomesTerminology | null;
}

export default function OrganizationChart({
  organizationData,
  outcomesTerminology
}: OrganizationChartProps) {
  return (
    <React.Fragment>
      <div>
        <h3 className="title organization-progress">
          {translate("app.orgProgressCard.orgProgress")}
        </h3>
        {organizationData.organizationApiError ? (
          <ApiError containerClasses="api-error" />
        ) : (
          <>
            <p className="org totals">
              {translate("app.orgProgressCard.orgTotal", {
                totalOrgs: organizationData.totalOrgs
              })}
            </p>

            <div id="org-charts-html">
              <OrgChart organizationData={organizationData} outcomesTerminology={outcomesTerminology} />
            </div>
          </>
        )}
      </div>
    </React.Fragment>
  );
}
interface OrgChartProps {
  organizationData: MissionAndOutcomesHtmlState;
  outcomesTerminology?: OutcomesTerminology | null;
}
const OrgChart = ({ organizationData, outcomesTerminology }: OrgChartProps) => {
  const formatMessage = useFormatMessage();
  const added = useIntl().formatMessage({
    id: "app.drilldown.added"
  });
  const notAdded = useIntl().formatMessage({
    id: "app.drilldown.notAdded"
  });
  const created = useIntl().formatMessage({
    id: "app.drilldown.created"
  });

  const notCreated = useIntl().formatMessage({
    id: "app.drilldown.notCreated"
  });

  const mapped = useIntl().formatMessage({
    id: "app.drilldown.mapped"
  });

  const notMapped = useIntl().formatMessage({
    id: "app.drilldown.notMapped"
  });
  const organizationsWithoutILO = useIntl().formatMessage({
    id: "app.orgProgressCard.organizationsWithoutILO"
  });
  const organizationsWithILO = useIntl().formatMessage({
    id: "app.orgProgressCard.organizationsWithILO"
  });
  const organizationsWithoutSO = formatMessage(
    { id: "app.orgProgressCard.organizationsWithoutSO" },
    { success_outcome_plural_lowercase: outcomesTerminology?.successOutcomes?.plural?.toLowerCase() || "..." }
  );
  const organizationsWithSO = formatMessage(
    { id: "app.orgProgressCard.organizationsWithSO" },
    { success_outcome_plural_lowercase: outcomesTerminology?.successOutcomes?.plural?.toLowerCase() || "..." }
  );
  const orgWithoutLO = formatMessage(
    { id: "app.orgProgressCard.orgWithoutLO" },
    { learning_outcome_plural_lowercase: outcomesTerminology?.learningOutcomes?.plural?.toLowerCase() || "..." }
  );
  const orgWithLO = formatMessage(
    { id: "app.orgProgressCard.orgWithLO" },
    { learning_outcome_plural_lowercase: outcomesTerminology?.learningOutcomes?.plural?.toLowerCase() || "..." }
  );
  const ILOMapped = useIntl().formatMessage({
    id: "app.orgProgressCard.ILOMapped"
  });
  const successOutcomes = formatMessage(
    { id: "app.orgProgressCard.successOutcomes" },
    { success_outcome_plural: outcomesTerminology?.successOutcomes?.plural || "..." }
  );
  const learningOutcomes = formatMessage(
    { id: "app.orgProgressCard.learningOutcomes" },
    { learning_outcome_plural: outcomesTerminology?.learningOutcomes?.plural || "..." }
  );
  const orgWithoutMission = useIntl().formatMessage({
    id: "app.orgProgressCard.orgWithoutMission"
  });
  const orgWithMission = useIntl().formatMessage({
    id: "app.orgProgressCard.orgWithMission"
  });
  const missionStatement = useIntl().formatMessage({
    id: "app.orgProgressCard.missionStatement"
  });

  return (
    <WmProgressMonitor
      id="organization-progress"
      print-mode={true}
      bar-labels-width="5rem"
    >
      <WmProgressIndicator label={missionStatement}>
        <WmProgressSlice
          legend={added}
          amount={organizationData.organizationMissionComplete.toString()}
          popoverTitle={popover(
            organizationData.organizationMissionComplete,
            organizationData.totalOrgs
          )}
          popoverText={orgWithMission}
        />
        <WmProgressSlice
          legend={notAdded}
          amount={organizationData.organizationMissionIncomplete.toString()}
          popoverTitle={popover(
            organizationData.organizationMissionIncomplete,
            organizationData.totalOrgs
          )}
          popoverText={orgWithoutMission}
        />
      </WmProgressIndicator>

      <WmProgressIndicator label={learningOutcomes}>
        <WmProgressSlice
          legend={created}
          amount={organizationData.learningOutcomes.toString()}
          popoverTitle={popover(
            organizationData.learningOutcomes,
            organizationData.totalOrgs
          )}
          popoverText={orgWithLO}
        />
        <WmProgressSlice
          legend={notCreated}
          amount={organizationData.learningOutcomesIncomplete.toString()}
          popoverTitle={popover(
            organizationData.learningOutcomesIncomplete,
            organizationData.totalOrgs
          )}
          popoverText={orgWithoutLO}
        />
      </WmProgressIndicator>

      <WmProgressIndicator label={successOutcomes}>
        <WmProgressSlice
          legend={created}
          amount={organizationData.otherOutcomes.toString()}
          popoverTitle={popover(
            organizationData.otherOutcomes,
            organizationData.totalOrgs
          )}
          popoverText={organizationsWithSO}
        />
        <WmProgressSlice
          legend={notCreated}
          amount={organizationData.otherOutcomesIncomplete.toString()}
          popoverTitle={popover(
            organizationData.otherOutcomesIncomplete,
            organizationData.totalOrgs
          )}
          popoverText={organizationsWithoutSO}
        />
      </WmProgressIndicator>

      <WmProgressIndicator label={ILOMapped}>
        <WmProgressSlice
          legend={mapped}
          amount={organizationData.iloMappings.toString()}
          popoverTitle={popover(
            organizationData.iloMappings,
            organizationData.totalOrgs
          )}
          popoverText={organizationsWithILO}
        />
        <WmProgressSlice
          legend={notMapped}
          popoverTitle={popover(
            organizationData.iloMappingsIncomplete,
            organizationData.totalOrgs
          )}
          popoverText={organizationsWithoutILO}
          amount={organizationData.iloMappingsIncomplete.toString()}
        />
      </WmProgressIndicator>
    </WmProgressMonitor>
  );
};
